import React, {useEffect, useState} from 'react'
import {Icon, Intent, Position} from '@blueprintjs/core'
import {gql} from '@apollo/client'
import {useLazyQuery} from '../../../Hooks/graphql_utils'
import {useDebounce} from '../../../Hooks/debounce'
import {Button, CoreMenu, FormGroup, InputGroup, Popover, Spinner, Tooltip} from '../../../Elements'
import {useNavigate} from 'react-router'

const JOB_ID_LAZY_QUERY = gql`
    query jobQuickSearchForIdQuery ($id: ID!) {
        job(id: $id) {
            id
        }
    }
`

const JOB_KEYWORD_LAZY_QUERY = gql`
    query jobQuickSearchKeywordQuery ($keyword: String!) {
        jobsByKeyword(keyword: $keyword) {
            id
            name
            address_line_1
        }
    }
`

export default ({disabled, label, errorText, groupStyles = {}, ...props}) => {
    const [searchKeyword, setSearchKeyword] = useState(null)

    const debounceDelay = 500
    const debouncedKeyword = useDebounce(searchKeyword, debounceDelay)
    const [jobIdSearch, setJobIdSearch] = useState(null)

    const [isDebouncing, setIsDebouncing] = useState(false)
    // Similar timeout logic from useDebounce for tracking debounce state
    useEffect(() => {
        const timeoutHandler = setTimeout(() => {
            setIsDebouncing(false)
        }, debounceDelay)

        return () => {
            clearTimeout(timeoutHandler)
        }
    }, [searchKeyword])

    const navigate = useNavigate()

    const [loadJobIdSearch, {data: jobIdData, loading: jobIdLoading}] = useLazyQuery(JOB_ID_LAZY_QUERY, {
        variables: {
            id: debouncedKeyword || ''
        }
    })

    const [loadJobKeywordSearch, {
        data: jobKeywordData, loading: jobKeywordLoading
    }] = useLazyQuery(JOB_KEYWORD_LAZY_QUERY, {
        variables: {
            keyword: debouncedKeyword || ''
        }
    })

    const loadProperLazyQuery = () => {
        if (!debouncedKeyword) {
            setJobIdSearch(null)
        } else if (/^\d+$/g.test(debouncedKeyword)) { // Check if var is integer
            setJobIdSearch(true)
            loadJobIdSearch()
        } else {
            setJobIdSearch(false)
            loadJobKeywordSearch()
        }
    }

    useEffect(loadProperLazyQuery, [debouncedKeyword])

    const targetJobId = jobIdData?.job?.id

    const tooltipContent = searchKeyword ? `No valid destination for '${searchKeyword}'` : 'Please enter a Job Number'

    const jobIdSearchButtonOnClick = () => {
        if (targetJobId && !isDebouncing && !jobIdLoading) navigate(`/job/${targetJobId}/edit`)
    }

    return (<form
            onSubmit={(e) => {
                // Specifically fires when Enter pressed on keyboard with the below InputGroup in focus
                e.preventDefault()
                jobIdSearchButtonOnClick()
            }}
        >
            <FormGroup
                disabled={disabled}
                label={label}
                helperText={errorText || props.helpText}
                intent={errorText ? Intent.DANGER : Intent.NONE}
                style={{margin: '1em 0 1em .25em', ...groupStyles}}
            >
                <Popover
                    isOpen={jobIdSearch === false}
                    position={Position.BOTTOM}
                >
                    <InputGroup
                        id='jobQuickSearch'
                        placeholder='Job ID or job name...'
                        value={searchKeyword || ''}
                        onChange={(evt) => {
                            setIsDebouncing(true)
                            setSearchKeyword(evt.target.value);
                        }}
                        rightElement={<Button minimal intent={Intent.PRIMARY}
                                              disabled={jobIdLoading || !targetJobId || jobIdSearch === false}
                                              onClick={jobIdSearchButtonOnClick}>
                            {jobIdLoading ? <Spinner size={10}/> : <Tooltip
                                content={tooltipContent}
                                position={Position.BOTTOM}
                                disabled={targetJobId || jobIdSearch === false}>
                                <Icon
                                    color={!debouncedKeyword || jobIdSearch === false ? 'grey' : targetJobId ? 'blue' : 'indianRed'}
                                    icon={!jobIdSearch ? 'double-chevron-right' : !debouncedKeyword || targetJobId ? 'double-chevron-right' : 'issue'}/>
                            </Tooltip>}
                        </Button>}
                    />
                    <div>
                        {jobKeywordLoading && <Spinner
                            size={50}
                            style={{
                                padding: 25
                            }}
                        />}
                        {jobKeywordData && <CoreMenu
                            items={jobKeywordData.jobsByKeyword}
                            menuItemLabelCallback={(x) => x.address_line_1 ? `${x.name} - ${x.address_line_1}` : `${x.name} - (No Address)`}
                            menuItemHrefCallback={(x) => `/job/${x.id}/edit`}
                            multiline={true}
                            intent={Intent.PRIMARY}
                            menuStyles={{
                                maxHeight: 400, overflowY: 'auto'
                            }}
                            menuItemStyles={{
                                flexDirection: 'column'
                            }}
                        />}
                    </div>
                </Popover>
            </FormGroup>
        </form>)
}